/**
 * useTvGuideSourceAvailable — whether a box-wide Live TV guide source exists.
 *
 * Reads `GET /api/streaming/guide/sources` (admin-only) so the TV Guide nav
 * entry can surface when a guide source is present box-wide even though the
 * viewing admin's own package set does not list it — e.g. a Dispatcharr install
 * owned by another user. `requiresAnyApp` on `installedApps` stays the fast
 * path; this is the box-wide fallback, fetched ONLY when enabled (admin +
 * WSDashboard on) so an ungated render never fires it. The result is cached at
 * module scope and de-duplicated across the sidebar and the page hook.
 */
import { useEffect, useState } from "react";

interface GuideSource {
  id: string;
  kind: string;
  label: string;
  state: string;
}

type GuideSourcesEnvelope = {
  ok?: boolean;
  data?: { available: boolean; sources: GuideSource[] };
  message?: string;
};

const CACHE_TTL_MS = 5 * 60 * 1000;
let cachedAvailable: boolean | null = null;
let cachedAt = 0;
let inflight: Promise<boolean> | null = null;

const fetchAvailable = async (): Promise<boolean> => {
  if (cachedAvailable !== null && Date.now() - cachedAt < CACHE_TTL_MS) {
    return cachedAvailable;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch("/api/streaming/guide/sources", {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const body = (await res.json().catch(() => null)) as GuideSourcesEnvelope | null;
      const available = res.ok && body?.ok === true && body.data?.available === true;
      cachedAvailable = available;
      cachedAt = Date.now();
      return available;
    } catch {
      cachedAvailable = false;
      cachedAt = Date.now();
      return false;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

/**
 * @param enabled only fetch when the viewer could see the TV Guide at all
 *   (admin AND WSDashboard on with a media server); `false` short-circuits to
 *   `false` and fires no request.
 */
export const useTvGuideSourceAvailable = (enabled: boolean): boolean => {
  const [available, setAvailable] = useState<boolean>(() => enabled && cachedAvailable === true);

  useEffect(() => {
    if (!enabled) {
      setAvailable(false);
      return;
    }
    let mounted = true;
    void fetchAvailable().then((value) => {
      if (mounted) setAvailable(value);
    });
    return () => {
      mounted = false;
    };
  }, [enabled]);

  return enabled && available;
};
